// apps/web/src/pages/compras/fornecedor-sheet.tsx — Fase 13, Plano 17 (COMP-01, D-18).
// FormDialog "Novo fornecedor" / "Editar fornecedor". Reusada na conferência da NF de compra (13-18) para "Confirmar fornecedor":
// salvar um fornecedor "A confirmar" o confirma como ativo (a regra é do servidor). Erros de negócio ficam no FormDialog.
// A autorização real é do servidor (compras:gerenciar); a escrita usa apiMutate (token CSRF).
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { RefreshCw, UserCheck } from 'lucide-react';
import type { FornecedorView, SalvarFornecedorInput } from '@pieltec/contracts';
import { Button } from '../../components/ui/button';
import { FormMessage } from '../../components/ui/form-message';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FormDialog, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../components/ui/form-dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { Textarea } from '../../components/ui/textarea';
import { apiMutate } from '../../lib/csrf-fetch';
import {
  MENSAGEM_CNPJ_INVALIDO,
  MENSAGEM_RAZAO_OBRIGATORIA,
  cnpjValido,
  mascararCnpjDigitando,
  mensagemErroFornecedor,
  somenteDigitos,
} from '../../lib/compras/cnpj';
import { lerErroApi } from '../../lib/vendas/erro-api';

export interface FornecedorSheetProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  /** null/ausente = "Novo fornecedor"; preenchido = "Editar fornecedor" (o detalhe é lido para trazer a versão). */
  readonly fornecedorId?: string | null;
  /** Valores iniciais do cadastro novo (ex.: dados do emitente da NF). */
  readonly inicial?: Partial<SalvarFornecedorInput>;
  /** Número da NF que originou o cadastro (usado no apoio "A confirmar" quando o detalhe não o traz). */
  readonly notaNumero?: string;
  readonly aoSalvar: (fornecedor: FornecedorView) => void;
}

interface Campos {
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string;
  inscricaoEstadual: string;
  email: string;
  telefone: string;
  contato: string;
  observacoes: string;
}

const CAMPOS_VAZIOS: Campos = {
  cnpj: '',
  razaoSocial: '',
  nomeFantasia: '',
  inscricaoEstadual: '',
  email: '',
  telefone: '',
  contato: '',
  observacoes: '',
};

function camposDe(origem: Partial<SalvarFornecedorInput> | FornecedorView | undefined): Campos {
  if (!origem) return CAMPOS_VAZIOS;
  return {
    cnpj: mascararCnpjDigitando(origem.cnpj ?? ''),
    razaoSocial: origem.razaoSocial ?? '',
    nomeFantasia: origem.nomeFantasia ?? '',
    inscricaoEstadual: origem.inscricaoEstadual ?? '',
    email: origem.email ?? '',
    telefone: origem.telefone ?? '',
    contato: origem.contato ?? '',
    observacoes: origem.observacoes ?? '',
  };
}

function opcional(valor: string): string | null {
  const limpo = valor.trim();
  return limpo === '' ? null : limpo;
}

export function FornecedorSheet({ aberto, onAbertoChange, fornecedorId = null, inicial, notaNumero, aoSalvar }: FornecedorSheetProps) {
  const edicao = fornecedorId !== null;
  const [campos, setCampos] = useState<Campos>(CAMPOS_VAZIOS);
  const [fornecedor, setFornecedor] = useState<FornecedorView | null>(null);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
  const [erroDetalhe, setErroDetalhe] = useState(false);
  const [erroCnpj, setErroCnpj] = useState<string | null>(null);
  const [erroRazao, setErroRazao] = useState<string | null>(null);
  const [erroApi, setErroApi] = useState<string | null>(null);
  const [versaoDesatualizada, setVersaoDesatualizada] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const carregarDetalhe = useCallback(
    async (id: string, sinal?: { vivo: boolean }) => {
      setCarregandoDetalhe(true);
      setErroDetalhe(false);
      try {
        const res = await fetch(`/api/v1/compras/fornecedores/${encodeURIComponent(id)}`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as FornecedorView;
        if (!sinal || sinal.vivo) {
          setFornecedor(json);
          setCampos(camposDe(json));
          setVersaoDesatualizada(false);
          setErroApi(null);
        }
      } catch {
        if (!sinal || sinal.vivo) setErroDetalhe(true);
      } finally {
        if (!sinal || sinal.vivo) setCarregandoDetalhe(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!aberto) return;
    const sinal = { vivo: true };
    setErroCnpj(null);
    setErroRazao(null);
    setErroApi(null);
    setVersaoDesatualizada(false);
    setFornecedor(null);
    if (fornecedorId) {
      setCampos(CAMPOS_VAZIOS);
      void carregarDetalhe(fornecedorId, sinal);
    } else {
      setCampos(camposDe(inicial));
      setErroDetalhe(false);
      setCarregandoDetalhe(false);
    }
    return () => {
      sinal.vivo = false;
    };
    // `inicial` é lido só na abertura do FormDialog: reabrir com outro valor recarrega; digitar não deve ser sobrescrito.
  }, [aberto, fornecedorId, carregarDetalhe]);

  function atualizar<K extends keyof Campos>(campo: K, valor: Campos[K]): void {
    setCampos((atual) => ({ ...atual, [campo]: valor }));
  }

  async function salvar(event: FormEvent): Promise<void> {
    event.preventDefault();
    const cnpjOk = cnpjValido(campos.cnpj);
    const razaoOk = campos.razaoSocial.trim() !== '';
    setErroCnpj(cnpjOk ? null : MENSAGEM_CNPJ_INVALIDO);
    setErroRazao(razaoOk ? null : MENSAGEM_RAZAO_OBRIGATORIA);
    setErroApi(null);
    if (!cnpjOk || !razaoOk) return;
    if (edicao && !fornecedor) return;

    const corpo: SalvarFornecedorInput = {
      cnpj: somenteDigitos(campos.cnpj),
      razaoSocial: campos.razaoSocial.trim(),
      nomeFantasia: opcional(campos.nomeFantasia),
      inscricaoEstadual: opcional(campos.inscricaoEstadual),
      email: opcional(campos.email),
      telefone: opcional(campos.telefone),
      contato: opcional(campos.contato),
      observacoes: opcional(campos.observacoes),
    };

    setSalvando(true);
    try {
      const res =
        edicao && fornecedor
          ? await apiMutate(`/api/v1/compras/fornecedores/${encodeURIComponent(fornecedor.id)}`, {
              method: 'PUT',
              body: { ...corpo, versao: fornecedor.versao } satisfies SalvarFornecedorInput,
            })
          : await apiMutate('/api/v1/compras/fornecedores', { body: corpo });
      if (!res.ok) {
        const falha = await lerErroApi(res, 'Não foi possível salvar o fornecedor.');
        if (falha.codigo === 'FORNECEDOR_CNPJ_INVALIDO') setErroCnpj(MENSAGEM_CNPJ_INVALIDO);
        else if (falha.codigo === 'FORNECEDOR_RAZAO_OBRIGATORIA') setErroRazao(MENSAGEM_RAZAO_OBRIGATORIA);
        else setErroApi(mensagemErroFornecedor(falha.codigo) ?? falha.mensagem);
        if (falha.codigo === 'FORNECEDOR_VERSAO_DESATUALIZADA') setVersaoDesatualizada(true);
        return;
      }
      const salvo = (await res.json()) as FornecedorView;
      aoSalvar(salvo);
      onAbertoChange(false);
    } catch {
      setErroApi('Não foi possível salvar o fornecedor. Verifique a conexão e tente novamente.');
    } finally {
      setSalvando(false);
    }
  }

  const aConfirmar = fornecedor?.situacao === 'A_CONFIRMAR' || (!edicao && inicial !== undefined && notaNumero !== undefined);
  const numeroNota = fornecedor?.criadoPorNotaNumero ?? notaNumero ?? null;
  const formularioPronto = !edicao || fornecedor !== null;

  return (
    <FormDialog open={aberto} onOpenChange={onAbertoChange}>
      <FormDialogContent>
        <form onSubmit={(e) => void salvar(e)} className="flex h-full flex-col gap-4" noValidate>
          <FormDialogHeader>
            <FormDialogTitle>{edicao ? 'Editar fornecedor' : 'Novo fornecedor'}</FormDialogTitle>
            <FormDialogDescription>Cadastro próprio do fornecedor. O CNPJ identifica o fornecedor nas NFs de compra.</FormDialogDescription>
          </FormDialogHeader>

          {edicao && carregandoDetalhe && (
            <div className="flex flex-col gap-3 px-4" aria-busy="true" aria-label="Carregando fornecedor">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          )}

          {edicao && erroDetalhe && (
            <div role="alert" className="flex flex-col items-start gap-3 px-4">
              <FormMessage>Não foi possível carregar o fornecedor. Verifique a conexão e tente novamente.</FormMessage>
              <Button type="button" variant="outline" onClick={() => fornecedorId && void carregarDetalhe(fornecedorId)}>
                <RefreshCw aria-hidden="true" />
                Tentar novamente
              </Button>
            </div>
          )}

          {formularioPronto && !carregandoDetalhe && !erroDetalhe && (
            <div className="flex flex-col gap-4 px-4">
              {aConfirmar && (
                <div className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <span className="inline-flex w-fit items-center gap-1 rounded-full border border-amber-200 bg-white px-2 py-0.5 text-xs font-medium text-amber-700">
                    <UserCheck className="size-3.5 shrink-0" aria-hidden="true" />A confirmar
                  </span>
                  <p className="text-sm text-amber-900">
                    Criado a partir do emitente da NF{numeroNota ? ` ${numeroNota}` : ''}. Confira os dados e salve para confirmar.
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-1">
                <Label htmlFor="fornecedor-cnpj">CNPJ *</Label>
                <Input
                  id="fornecedor-cnpj"
                  inputMode="numeric"
                  autoComplete="off"
                  value={campos.cnpj}
                  onChange={(e) => atualizar('cnpj', mascararCnpjDigitando(e.target.value))}
                  aria-invalid={erroCnpj !== null}
                  aria-describedby={erroCnpj ? 'fornecedor-cnpj-erro' : undefined}
                />
                {erroCnpj && <FormMessage id="fornecedor-cnpj-erro">{erroCnpj}</FormMessage>}
              </div>

              <div className="flex flex-col gap-1">
                <Label htmlFor="fornecedor-razao">Razão social *</Label>
                <Input
                  id="fornecedor-razao"
                  maxLength={200}
                  value={campos.razaoSocial}
                  onChange={(e) => atualizar('razaoSocial', e.target.value)}
                  aria-invalid={erroRazao !== null}
                  aria-describedby={erroRazao ? 'fornecedor-razao-erro' : undefined}
                />
                {erroRazao && <FormMessage id="fornecedor-razao-erro">{erroRazao}</FormMessage>}
              </div>

              <div className="flex flex-col gap-1">
                <Label htmlFor="fornecedor-fantasia">Nome fantasia</Label>
                <Input id="fornecedor-fantasia" maxLength={200} value={campos.nomeFantasia} onChange={(e) => atualizar('nomeFantasia', e.target.value)} />
              </div>

              <div className="flex flex-col gap-1">
                <Label htmlFor="fornecedor-ie">Inscrição estadual</Label>
                <Input id="fornecedor-ie" maxLength={30} value={campos.inscricaoEstadual} onChange={(e) => atualizar('inscricaoEstadual', e.target.value)} />
              </div>

              <div className="flex flex-col gap-1">
                <Label htmlFor="fornecedor-email">E-mail</Label>
                <Input id="fornecedor-email" type="email" maxLength={200} value={campos.email} onChange={(e) => atualizar('email', e.target.value)} />
              </div>

              <div className="flex flex-col gap-1">
                <Label htmlFor="fornecedor-telefone">Telefone</Label>
                <Input id="fornecedor-telefone" type="tel" maxLength={40} value={campos.telefone} onChange={(e) => atualizar('telefone', e.target.value)} />
              </div>

              <div className="flex flex-col gap-1">
                <Label htmlFor="fornecedor-contato">Contato</Label>
                <Input id="fornecedor-contato" maxLength={120} value={campos.contato} onChange={(e) => atualizar('contato', e.target.value)} />
              </div>

              <div className="flex flex-col gap-1">
                <Label htmlFor="fornecedor-observacoes">Observações</Label>
                <Textarea id="fornecedor-observacoes" maxLength={2000} value={campos.observacoes} onChange={(e) => atualizar('observacoes', e.target.value)} />
              </div>

              {erroApi && (
                <div role="alert" className="flex flex-col items-start gap-2">
                  <FormMessage>{erroApi}</FormMessage>
                  {versaoDesatualizada && fornecedorId && (
                    <Button type="button" variant="outline" size="sm" onClick={() => void carregarDetalhe(fornecedorId)}>
                      <RefreshCw aria-hidden="true" />
                      Recarregar fornecedor
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          <FormDialogFooter>
            <Button type="submit" disabled={salvando || !formularioPronto || carregandoDetalhe || erroDetalhe}>
              {salvando ? 'Salvando...' : 'Salvar fornecedor'}
            </Button>
            <Button type="button" variant="outline" onClick={() => onAbertoChange(false)}>
              Cancelar
            </Button>
          </FormDialogFooter>
        </form>
      </FormDialogContent>
    </FormDialog>
  );
}
